import { Color } from './java/Color';
import { Random } from './java/Random';
import { GameObject } from './GameObject';
import { Handler } from './Handler';
import { HUD } from './HUD';
import { Game } from './Game';
import { ID } from './ID';
import { BasicEnemy } from './BasicEnemy';
import { FastEnemy } from './FastEnemy';
import { SmartEnemy } from './SmartEnemy';
import { HardEnemy } from './HardEnemy';
import { EnemyBoss } from './EnemyBoss';
import { BossLaser } from './BossLaser';
import { BossSwarm } from './BossSwarm';
import { BossSplitter } from './BossSplitter';
import { SpawnTelegraph } from './SpawnTelegraph';

export class Spawn {

  private handler: Handler;
  private hud: HUD;
  private game: Game;
  private r: Random = new Random();

  /** Set a deterministic seed for daily challenge mode */
  setSeed(seed: number | bigint): void {
    this.r = new Random(seed);
  }

  private scoreKeep = 0;
  private bossActive = false;

  getLevelProgress(): number { return this.scoreKeep / 250; }
  isBossActive(): boolean { return this.bossActive; }

  // Enemy colors for telegraphs
  private static readonly C_BASIC = new Color(235, 87, 87);
  private static readonly C_FAST = new Color(78, 205, 196);
  private static readonly C_SMART = new Color(199, 125, 255);
  private static readonly C_HARD = new Color(245, 195, 68);

  // Last scripted level per difficulty — endless kicks in after this
  private static readonly SCRIPT_END_NORMAL = 25;
  private static readonly SCRIPT_END_HARD = 15;
  private static readonly SCRIPT_END_INSANE = 20;

  constructor(handler: Handler, hud: HUD, game: Game) {
    this.handler = handler;
    this.hud = hud;
    this.game = game;
  }

  tick(): void {
    // Check if boss has been defeated
    if (this.bossActive) {
      if (this.checkBossDefeated()) return;
    }

    this.scoreKeep++;

    if (this.scoreKeep >= 250) {
      this.scoreKeep = 0;
      this.hud.setLevel(this.hud.getLevel() + 1);
      Game.triggerLevelUp();
      this.hud.triggerLevelUpBanner();

      const level = this.hud.getLevel();

      // Boss every 10th level — universal across all difficulties
      if (level % 10 === 0 && level > 0) {
        // Scale boss HP: base 1200, +200 per boss encounter
        const bossNum = Math.trunc(level / 10);
        const bossHp = 1200 + (bossNum - 1) * 200;
        this.triggerBossSpawn(bossHp);
      } else if (this.game.diff === 3) {
        this.spawnCombatDifficulty();
      } else if (this.game.diff === 0) {
        this.spawnNormalDifficulty();
      } else if (this.game.diff === 1) {
        this.spawnHardDifficulty();
      } else if (this.game.diff === 2) {
        this.spawnInsaneDifficulty();
      } else if (this.game.diff === 4) {
        this.spawnNoPlayerDifficulty();
      }
    }
  }

  private rx(): number { return this.r.nextInt(Game.WIDTH - 80) + 20; }
  private ry(): number { return this.r.nextInt(Game.HEIGHT - 80) + 20; }

  /** Check all boss types for defeat. Returns true if a boss is still alive. */
  private checkBossDefeated(): boolean {
    let anyAlive = false;

    for (let i = this.handler.getObjects().length - 1; i >= 0; i--) {
      const obj: GameObject = this.handler.getObjects()[i];

      if (obj instanceof EnemyBoss) {
        const boss: EnemyBoss = obj;
        if (boss.isDefeated()) {
          this.handler.removeObject(boss);
          this.onBossDefeated();
          return false;
        }
        anyAlive = true;
      } else if (obj instanceof BossLaser) {
        const boss: BossLaser = obj;
        if (boss.isDefeated()) {
          this.handler.removeObject(boss);
          this.onBossDefeated();
          return false;
        }
        anyAlive = true;
      } else if (obj instanceof BossSwarm) {
        const boss: BossSwarm = obj;
        if (boss.isDefeated()) {
          this.handler.removeObject(boss);
          this.onBossDefeated();
          return false;
        }
        anyAlive = true;
      } else if (obj instanceof BossSplitter) {
        const bs: BossSplitter = obj;
        if (bs.isFragmentDead()) {
          this.handler.removeObject(bs);
        } else if (!bs.isFragmentDead()) {
          anyAlive = true;
        }
      }
    }

    // Splitter special case: boss is defeated when ALL fragments are gone
    if (!anyAlive && this.bossActive) {
      // Check if there were splitter fragments that just finished dying
      this.onBossDefeated();
      return false;
    }

    return anyAlive;
  }

  private onBossDefeated(): void {
    this.bossActive = false;
    Game.addBossDefeated();
    this.hud.triggerWaveAnnounce();
    if (Game.combatMode) {
      this.spawnCombatTelegraph(new BasicEnemy(this.rx(), this.ry(), ID.BasicEnemy, this.handler), Spawn.C_BASIC);
      this.spawnCombatTelegraph(new FastEnemy(this.rx(), this.ry(), ID.FastEnemy, this.handler), Spawn.C_FAST);
    } else {
      this.spawnTelegraph(new BasicEnemy(this.rx(), this.ry(), ID.BasicEnemy, this.handler), Spawn.C_BASIC);
      this.spawnTelegraph(new FastEnemy(this.rx(), this.ry(), ID.FastEnemy, this.handler), Spawn.C_FAST);
    }
  }

  private triggerBossSpawn(bossHp: number = 1200): void {
    // Collect enemy positions for explosion particles
    const particles: number[][] = [];
    for (let i = 0; i < this.handler.getObjects().length; i++) {
      const obj: GameObject = this.handler.getObjects()[i];
      if (obj.getId() === ID.Player || obj.getId() === ID.Trail || obj.getId() === ID.SpawnTelegraph) continue;
      const ex = obj.getX() + 16;
      const ey = obj.getY() + 16;
      let c: Color = Spawn.C_BASIC;
      if (obj.getId() === ID.FastEnemy) c = Spawn.C_FAST;
      else if (obj.getId() === ID.SmartEnemy) c = Spawn.C_SMART;
      else if (obj.getId() === ID.HardEnemy) c = Spawn.C_HARD;
      for (let j = 0; j < 6; j++) {
        const angle = this.r.nextFloat() * Math.PI * 2;
        const speed = 3 + this.r.nextFloat() * 5;
        const size = 3 + this.r.nextFloat() * 6;
        particles.push([
          ex, ey,
          Math.cos(angle) * speed, Math.sin(angle) * speed,
          size, c.getRed(), c.getGreen(), c.getBlue(), 1,
        ]);
      }
    }
    Game.setEnemyExplosions(particles);

    this.handler.clearEnemys();
    Game.triggerBossIntro();

    // Refill 20% health on boss level
    const maxHealth = 100 + Math.trunc(this.hud.bounds / 2);
    HUD.HEALTH = Math.min(HUD.HEALTH + maxHealth * 0.2, maxHealth);

    // Pick boss type based on level and difficulty
    const level = this.hud.getLevel();
    const boss: GameObject = this.pickBoss(level, bossHp);
    this.handler.addObject(boss);
    this.bossActive = true;
  }

  /**
   * Boss rotation:
   * - Level 10: always classic EnemyBoss (first encounter, teach the mechanic)
   * - Level 20: Splitter on Normal, Laser on Hard, Swarm on Insane
   * - Level 30+: cycle through all 4 types
   * The cycle is: Classic → Splitter → Laser → Swarm → repeat
   */
  private pickBoss(level: number, bossHp: number): GameObject {
    const bossNum = Math.trunc(level / 10); // 1, 2, 3, 4, ...

    let type: number;
    if (bossNum === 1) {
      type = 0; // Always classic first
    } else if (bossNum === 2) {
      // Second boss depends on difficulty
      type = this.game.diff === 0 ? 1 : this.game.diff === 1 ? 2 : 3;
    } else {
      // Cycle through all 4
      type = (bossNum - 1) % 4;
    }

    const spawnX = Math.trunc(Game.WIDTH / 2) - 48;
    const spawnY = -120;

    switch (type) {
      case 1: {
        const bs = new BossSplitter(spawnX, spawnY, ID.EnemyBoss, this.handler, 0);
        bs.setMaxHp(bossHp * 0.75); // Splitter total HP is spread across fragments
        return bs;
      }
      case 2: {
        const bl = new BossLaser(spawnX, spawnY, ID.EnemyBoss, this.handler);
        bl.setMaxHp(bossHp * 1.15); // Laser boss is stationary, so a bit more HP
        return bl;
      }
      case 3: {
        const bw = new BossSwarm(spawnX, spawnY, ID.EnemyBoss, this.handler);
        bw.setMaxHp(bossHp * 0.65); // Swarm boss is fragile
        return bw;
      }
      default: {
        const eb = new EnemyBoss(spawnX, spawnY, ID.EnemyBoss, this.handler);
        eb.setMaxHp(bossHp);
        return eb;
      }
    }
  }

  private spawnTelegraph(enemy: GameObject, color: Color): void {
    this.handler.addObject(new SpawnTelegraph(enemy.getX(), enemy.getY(), this.handler, enemy, color, 32));
  }

  /** Spawn an enemy with scaled velocity, then telegraph it. */
  private spawnScaled(enemy: GameObject, color: Color, speedMult: number): void {
    enemy.setVelX(enemy.getVelX() * speedMult);
    enemy.setVelY(enemy.getVelY() * speedMult);
    this.spawnTelegraph(enemy, color);
  }

  private explodeClearEnemys(): void {
    const particles: number[][] = [];
    for (let i = 0; i < this.handler.getObjects().length; i++) {
      const obj: GameObject = this.handler.getObjects()[i];
      const id: ID = obj.getId();
      if (id === ID.Player || id === ID.Trail || id === ID.SpawnTelegraph) continue;
      const ex = obj.getX() + 16;
      const ey = obj.getY() + 16;
      let c: Color = Spawn.C_BASIC;
      if (id === ID.FastEnemy) c = Spawn.C_FAST;
      else if (id === ID.SmartEnemy) c = Spawn.C_SMART;
      else if (id === ID.HardEnemy) c = Spawn.C_HARD;
      else if (id === ID.EnemyBoss) c = Spawn.C_BASIC;
      for (let j = 0; j < 5; j++) {
        const angle = this.r.nextFloat() * Math.PI * 2;
        const speed = 2 + this.r.nextFloat() * 4;
        const size = 2 + this.r.nextFloat() * 5;
        particles.push([
          ex, ey,
          Math.cos(angle) * speed, Math.sin(angle) * speed,
          size, c.getRed(), c.getGreen(), c.getBlue(), 0.8,
        ]);
      }
    }
    if (particles.length !== 0) {
      Game.setEnemyExplosions(particles);
    }
    this.handler.clearEnemys();
  }

  // ==================== Combat mode ====================

  private static readonly SCRIPT_END_COMBAT = 20;

  // Combat HP values per enemy type
  private static readonly HP_BASIC = 30;
  private static readonly HP_FAST = 20;
  private static readonly HP_HARD = 50;
  private static readonly HP_SMART = 40;

  private setCombatHp(enemy: GameObject): void {
    if (enemy instanceof BasicEnemy) enemy.setCombatHp(Spawn.HP_BASIC);
    else if (enemy instanceof FastEnemy) enemy.setCombatHp(Spawn.HP_FAST);
    else if (enemy instanceof HardEnemy) enemy.setCombatHp(Spawn.HP_HARD);
    else if (enemy instanceof SmartEnemy) enemy.setCombatHp(Spawn.HP_SMART);
  }

  private spawnCombatTelegraph(enemy: GameObject, color: Color): void {
    this.setCombatHp(enemy);
    this.spawnTelegraph(enemy, color);
  }

  private spawnCombatScaled(enemy: GameObject, color: Color, speedMult: number): void {
    this.setCombatHp(enemy);
    this.spawnScaled(enemy, color, speedMult);
  }

  private spawnCombatDifficulty(): void {
    const level = this.hud.getLevel();
    if (level > Spawn.SCRIPT_END_COMBAT) {
      this.spawnCombatEndless(level);
      return;
    }
    // Scripted combat levels — more enemies, faster pacing
    switch (level) {
      case 2:
      case 3:
        this.spawnCombatTelegraph(new BasicEnemy(this.rx(), this.ry(), ID.BasicEnemy, this.handler), Spawn.C_BASIC);
        break;
      case 4:
        this.spawnCombatTelegraph(new FastEnemy(this.rx(), this.ry(), ID.FastEnemy, this.handler), Spawn.C_FAST);
        break;
      case 5:
        this.spawnCombatTelegraph(new BasicEnemy(this.rx(), this.ry(), ID.BasicEnemy, this.handler), Spawn.C_BASIC);
        this.spawnCombatTelegraph(new FastEnemy(this.rx(), this.ry(), ID.FastEnemy, this.handler), Spawn.C_FAST);
        break;
      case 6:
        this.spawnCombatTelegraph(new HardEnemy(this.rx(), this.ry(), ID.HardEnemy, this.handler), Spawn.C_HARD);
        break;
      case 7:
        this.spawnCombatTelegraph(new SmartEnemy(this.rx(), this.ry(), ID.SmartEnemy, this.handler), Spawn.C_SMART);
        break;
      case 8:
      case 9:
        this.spawnCombatTelegraph(new BasicEnemy(this.rx(), this.ry(), ID.BasicEnemy, this.handler), Spawn.C_BASIC);
        this.spawnCombatTelegraph(new HardEnemy(this.rx(), this.ry(), ID.HardEnemy, this.handler), Spawn.C_HARD);
        break;
      case 11: case 12: case 13:
        this.spawnCombatTelegraph(new FastEnemy(this.rx(), this.ry(), ID.FastEnemy, this.handler), Spawn.C_FAST);
        this.spawnCombatTelegraph(new BasicEnemy(this.rx(), this.ry(), ID.BasicEnemy, this.handler), Spawn.C_BASIC);
        break;
      case 14:
        this.spawnCombatTelegraph(new SmartEnemy(this.rx(), this.ry(), ID.SmartEnemy, this.handler), Spawn.C_SMART);
        this.spawnCombatTelegraph(new HardEnemy(this.rx(), this.ry(), ID.HardEnemy, this.handler), Spawn.C_HARD);
        break;
      case 15:
        this.explodeClearEnemys();
        this.spawnCombatTelegraph(new FastEnemy(this.rx(), this.ry(), ID.FastEnemy, this.handler), Spawn.C_FAST);
        this.spawnCombatTelegraph(new FastEnemy(this.rx(), this.ry(), ID.FastEnemy, this.handler), Spawn.C_FAST);
        break;
      case 16: case 17: case 18: case 19:
        this.spawnCombatTelegraph(new BasicEnemy(this.rx(), this.ry(), ID.BasicEnemy, this.handler), Spawn.C_BASIC);
        if (level % 2 === 0) this.spawnCombatTelegraph(new HardEnemy(this.rx(), this.ry(), ID.HardEnemy, this.handler), Spawn.C_HARD);
        break;
    }
  }

  private spawnCombatEndless(level: number): void {
    const wave = level - Spawn.SCRIPT_END_COMBAT;
    const speedMult = 1.0 + Math.log(1 + wave * 0.1) * 0.6;

    // Purge every 12 waves in combat
    if (wave % 12 === 0) {
      this.explodeClearEnemys();
      let packSize = 3 + Math.trunc(wave * 0.08);
      packSize = Math.min(packSize, 5);
      for (let i = 0; i < packSize; i++) {
        this.spawnCombatWeightedEnemy(wave, speedMult);
      }
      return;
    }

    // Combat spawns more enemies — 2 base, sometimes 3
    let count = 2;
    if (wave >= 8 && this.r.nextFloat() < Math.min(0.5, wave * 0.03)) count = 3;

    for (let i = 0; i < count; i++) {
      this.spawnCombatWeightedEnemy(wave, speedMult);
    }
  }

  private spawnCombatWeightedEnemy(wave: number, speedMult: number): void {
    const t = wave * 0.09;
    const wBasic = Math.max(10, 35 - t * 2);
    const wFast = 30 + Math.min(5, t * 0.8);
    const wHard = Math.min(30, 15 + t * 1.5);
    const wSmart = Math.min(25, 10 + t * 1.2);

    const total = wBasic + wFast + wHard + wSmart;
    const roll = this.r.nextFloat() * total;

    if (roll < wBasic) {
      this.spawnCombatScaled(new BasicEnemy(this.rx(), this.ry(), ID.BasicEnemy, this.handler), Spawn.C_BASIC, speedMult);
    } else if (roll < wBasic + wFast) {
      this.spawnCombatScaled(new FastEnemy(this.rx(), this.ry(), ID.FastEnemy, this.handler), Spawn.C_FAST, speedMult);
    } else if (roll < wBasic + wFast + wHard) {
      this.spawnCombatScaled(new HardEnemy(this.rx(), this.ry(), ID.HardEnemy, this.handler), Spawn.C_HARD, speedMult);
    } else {
      this.spawnCombatTelegraph(new SmartEnemy(this.rx(), this.ry(), ID.SmartEnemy, this.handler), Spawn.C_SMART);
    }
  }

  // ==================== Scripted levels ====================

  private spawnNormalDifficulty(): void {
    const level = this.hud.getLevel();
    if (level > Spawn.SCRIPT_END_NORMAL) {
      this.spawnEndless(level, Spawn.SCRIPT_END_NORMAL, 0);
      return;
    }
    switch (level) {
      case 2:
      case 3:
        this.spawnTelegraph(new BasicEnemy(this.rx(), this.ry(), ID.BasicEnemy, this.handler), Spawn.C_BASIC); break;
      case 4:
      case 6:
      case 8:
        this.spawnTelegraph(new FastEnemy(this.rx(), this.ry(), ID.FastEnemy, this.handler), Spawn.C_FAST); break;
      case 5:
      case 25:
        this.spawnTelegraph(new SmartEnemy(this.rx(), this.ry(), ID.SmartEnemy, this.handler), Spawn.C_SMART); break;
      case 15:
        this.explodeClearEnemys();
        this.spawnTelegraph(new FastEnemy(this.rx(), this.ry(), ID.FastEnemy, this.handler), Spawn.C_FAST); break;
      case 17:
        this.spawnTelegraph(new FastEnemy(this.rx(), this.ry(), ID.FastEnemy, this.handler), Spawn.C_FAST); break;
      case 18:
      case 20:
      case 22:
        this.spawnTelegraph(new BasicEnemy(this.rx(), this.ry(), ID.BasicEnemy, this.handler), Spawn.C_BASIC); break;
    }
  }

  private spawnHardDifficulty(): void {
    const level = this.hud.getLevel();
    if (level > Spawn.SCRIPT_END_HARD) {
      this.spawnEndless(level, Spawn.SCRIPT_END_HARD, 1);
      return;
    }
    switch (level) {
      case 2:
      case 6:
        this.spawnTelegraph(new HardEnemy(this.rx(), this.ry(), ID.HardEnemy, this.handler), Spawn.C_HARD); break;
      case 3:
        this.spawnTelegraph(new SmartEnemy(this.rx(), this.ry(), ID.SmartEnemy, this.handler), Spawn.C_SMART); break;
      case 4:
      case 5:
        this.spawnTelegraph(new BasicEnemy(this.rx(), this.ry(), ID.BasicEnemy, this.handler), Spawn.C_BASIC); break;
    }
  }

  private spawnInsaneDifficulty(): void {
    const level = this.hud.getLevel();
    if (level > Spawn.SCRIPT_END_INSANE) {
      this.spawnEndless(level, Spawn.SCRIPT_END_INSANE, 2);
      return;
    }
    switch (level) {
      case 2:
      case 3:
      case 10:
        this.spawnTelegraph(new FastEnemy(this.rx(), this.ry(), ID.FastEnemy, this.handler), Spawn.C_FAST); break;
      case 4:
        this.spawnTelegraph(new SmartEnemy(this.rx(), this.ry(), ID.SmartEnemy, this.handler), Spawn.C_SMART); break;
      case 5:
      case 7:
      case 20:
        this.spawnTelegraph(new HardEnemy(this.rx(), this.ry(), ID.HardEnemy, this.handler), Spawn.C_HARD); break;
      case 8:
      case 12:
        this.spawnTelegraph(new BasicEnemy(this.rx(), this.ry(), ID.BasicEnemy, this.handler), Spawn.C_BASIC); break;
    }
  }

  private spawnNoPlayerDifficulty(): void {
    const level = this.hud.getLevel();
    switch (level) {
      case 2:
        this.spawnTelegraph(new FastEnemy(this.rx(), this.ry(), ID.FastEnemy, this.handler), Spawn.C_FAST); break;
      case 3:
        this.spawnTelegraph(new HardEnemy(this.rx(), this.ry(), ID.HardEnemy, this.handler), Spawn.C_HARD); break;
      case 4:
        this.spawnTelegraph(new SmartEnemy(this.rx(), this.ry(), ID.SmartEnemy, this.handler), Spawn.C_SMART); break;
    }
  }

  // ==================== Endless scaling ====================

  /**
   * Formula-based endless spawner. Called for every level past the scripted content.
   * Boss levels (every 10th) are already handled in tick(), so this only handles non-boss levels.
   *
   * @param level      Current level number
   * @param scriptEnd  Last scripted level for this difficulty
   * @param difficulty 0=normal, 1=hard, 2=insane
   */
  private spawnEndless(level: number, scriptEnd: number, difficulty: number): void {
    const wave = level - scriptEnd; // 1, 2, 3, ...

    // Difficulty escalation multiplier
    const diffMult = difficulty === 0 ? 1.0 : difficulty === 1 ? 1.25 : 1.5;

    // Speed multiplier: gentle logarithmic growth, caps around ~1.8x
    const speedMult = 1.0 + Math.log(1 + wave * diffMult * 0.08) * 0.7;

    // Purge wave — every 15 waves, clears field and spawns a modest replacement set
    if (wave % 15 === 0) {
      this.explodeClearEnemys();
      let packSize = 2 + Math.trunc(wave * diffMult * 0.06);
      packSize = Math.min(packSize, 4);
      for (let i = 0; i < packSize; i++) {
        this.spawnWeightedEnemy(wave, diffMult, speedMult);
      }
      return;
    }

    // Regular wave — spawn 1 enemy, occasionally 2 at higher waves
    let count = 1;
    // Second enemy starts appearing around wave 6, becomes reliable around wave 15
    if (wave >= 6 && this.r.nextFloat() < Math.min(0.6, wave * diffMult * 0.025)) {
      count = 2;
    }

    for (let i = 0; i < count; i++) {
      this.spawnWeightedEnemy(wave, diffMult, speedMult);
    }
  }

  /**
   * Spawn a single enemy with type chosen by weighted random based on wave progression.
   * Type distribution shifts gradually — Basic stays common early, harder types creep in.
   */
  private spawnWeightedEnemy(wave: number, diffMult: number, speedMult: number): void {
    // Progression factor: gentle ramp
    const t = wave * diffMult * 0.07;

    // Basic:  dominant early (40), fades slowly to a floor (10)
    // Fast:   steady presence (25), slight rise then holds
    // Hard:   starts low (15), rises gradually (30 cap)
    // Smart:  starts low (10), rises slowly (25 cap)
    const wBasic = Math.max(10, 40 - t * 2);
    const wFast = 25 + Math.min(5, t * 0.8);
    const wHard = Math.min(30, 15 + t * 1.5);
    const wSmart = Math.min(25, 10 + t * 1.2);

    const total = wBasic + wFast + wHard + wSmart;
    const roll = this.r.nextFloat() * total;

    if (roll < wBasic) {
      this.spawnScaled(new BasicEnemy(this.rx(), this.ry(), ID.BasicEnemy, this.handler), Spawn.C_BASIC, speedMult);
    } else if (roll < wBasic + wFast) {
      this.spawnScaled(new FastEnemy(this.rx(), this.ry(), ID.FastEnemy, this.handler), Spawn.C_FAST, speedMult);
    } else if (roll < wBasic + wFast + wHard) {
      this.spawnScaled(new HardEnemy(this.rx(), this.ry(), ID.HardEnemy, this.handler), Spawn.C_HARD, speedMult);
    } else {
      // SmartEnemy tracks via distance formula, speed scaling doesn't apply the same way
      // Instead, just spawn it — its tracking naturally gets harder with more enemies on screen
      this.spawnTelegraph(new SmartEnemy(this.rx(), this.ry(), ID.SmartEnemy, this.handler), Spawn.C_SMART);
    }
  }
}
